//
// # ReconnectDialog
//
// Modal shown when AlphaClient declares the WebSocket connection lost.
// Non-dismissible: the only resolution is to reload the page, which gives
// us a fresh server state (handles crash/restart), a fresh client bundle
// (handles version skew), and a clean webpack-dev session.
//
// Phase 1 only restores the connection via reload — client UI state
// (open tabs, viewport, panel layout) is lost on reload. See Phase 2
// in the design plan for true session resume.
//

import React, { useCallback } from "react";

import { Modal } from "./Modal/Modal";
import type { ReconnectDialogState } from "../types";

interface MessageCopy {
  readonly title: string;
  readonly body: string;
}

// Per-reason copy. Kept terse — the user only needs to know what happened
// and that they should reload. Verbose explanations belong in the docs.
const COPY: Record<ReconnectDialogState["reason"], MessageCopy> = {
  "retries-exhausted": {
    title: "Connection Lost",
    body: "The network connection to the server was lost and could not be restored. Reload to reconnect.",
  },
  "server-restarted": {
    title: "Server Restarted",
    body: "The server restarted while you were connected. Reload to start a new session.",
  },
  "client-unknown": {
    title: "Session Expired",
    body: "The server no longer recognises this session. Reload to start a new session.",
  },
};

export interface ReconnectDialogProps {
  readonly state: ReconnectDialogState | null;
}

export function ReconnectDialog({ state }: ReconnectDialogProps) {
  const handleReload = useCallback(() => {
    window.location.reload();
  }, []);

  // onClose is a no-op (the modal is non-dismissible), but Modal's API
  // requires the prop. We pass an empty function rather than asserting.
  const noop = useCallback(() => {
    // Intentionally empty: the modal is non-dismissible.
  }, []);

  const copy = state ? COPY[state.reason] : null;

  return (
    <Modal
      open={state !== null}
      onClose={noop}
      title={copy?.title ?? ""}
      dismissible={false}
    >
      <div className="flex flex-col gap-4">
        <p className="text-sm" style={{ color: "var(--color-ide-text-muted)" }}>
          {copy?.body ?? ""}
        </p>

        <div className="flex justify-end gap-2">
          <button
            onClick={handleReload}
            className="px-3 py-1.5 text-xs rounded font-semibold"
            style={{
              backgroundColor: "var(--color-ide-accent, #3b82f6)",
              color: "white",
            }}
          >
            Reload
          </button>
        </div>
      </div>
    </Modal>
  );
}
